import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Typography,
  DialogContent,
  DialogActions,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  TextField,
} from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import { dateLocale } from "@icarius-localization/strings";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import { dateIsAfterToday, createDateFromDDMMYYYY, formatDate, getTomorrow } from "@icarius-utils/date";
import { getLocalizedString } from "@icarius-localization/strings";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';
import SelectionDialog from "@icarius-common/selectionDialog";

const gridStyle = { minHeight: 75 };

const NewPayRiseContent = (props) => {

  const {
    editData,
    isForEdit,
    requestCode,
    colaborators,
    raiseCalculationData,
    isReadOnly,
    recipients,
    handleNoRecipient,
    handleRequest,
    handleClose,
  } = props;


  useEffect(() => {
    if (!isReadOnly && !isForEdit && recipients?.raise?.length === 0) {
      handleNoRecipient();
      handleClose();
    }
  }, [isReadOnly, isForEdit, recipients, handleNoRecipient, handleClose])

  const dispatch = useDispatch();
  const [userSelectionDialogIsOpen, setUserSelectionDialogIsOpen] = useState(false);
  const [formData, setFormData] = useState(() => {
    const initialActualSalary = (() => {
      if (isForEdit) return editData?.current_salary;
      if (isReadOnly) {
        const collaborator = colaborators.find((colab) => colab.value === editData?.colaborator_name);
        return collaborator?.salary || "";
      }

      return "";
    })();

    const initialActualSalaryCurrency = (() => {
      if (isForEdit) return editData?.currency;
      if (isReadOnly) {
        const collaborator = colaborators.find((colab) => colab.value === editData?.colaborator_name);
        return collaborator?.currency || "";
      }

      return "";
    })();

    return {
      date: editData?.expected_date ? createDateFromDDMMYYYY(editData.expected_date) : null,
      descripcion: editData?.comments || "",
      amount: editData?.value?.toString() || "",
      legalType: editData?.reason_id || "",
      colaborator: editData?.colaborator_name || "",
      actualSalary: initialActualSalary,
      actualSalaryCurrency: initialActualSalaryCurrency,
      receptor: recipients?.raise?.length === 1 ? recipients.raise[0].code : "", //si tiene un valor solo, inicializar con ese
    };
  })

  const setFormValue = (value, fieldName) => {
    if (fieldName === "colaborator") {
      const newActualSalary = (() => {
        const collaborator = colaborators.find((colab) => colab.code === value);
        if (collaborator) return collaborator.salary;
        return "";
      })();

      const newActualSalaryCurrency = (() => {
        const collaborator = colaborators.find((colab) => colab.code === value);
        if (collaborator) return collaborator.currency;
        return "";
      })();

      setFormData({
        ...formData,
        [fieldName]: value,
        actualSalary: newActualSalary,
        actualSalaryCurrency: newActualSalaryCurrency,
      });
      return;
    }

    setFormData({
      ...formData,
      [fieldName]: value,
    })
  }

  const handleCloseAndReload = (shouldReloadTable) => {
    if (isForEdit && shouldReloadTable) {
      handleClose(true);
    } else {
      handleClose();
    }
  };

  const textIsValid = () => {
    return formData.descripcion && formData.descripcion.length > 0 && formData.descripcion.length <= 1024;
  };

  const dataIsComplete = (checkAmount = true) => {
    if (isForEdit) {
      if (dateIsAfterToday(formData.date) && textIsValid() && formData.legalType) return true;
      return false;
    }

    if (!isForEdit && !isReadOnly && !formData.receptor) return false;

    if (checkAmount) {
      if (formData.legalType && formData.colaborator && isAmountInRange() && textIsValid() && dateIsAfterToday(formData.date)) return true;
    } else {
      if (formData.legalType && formData.colaborator && textIsValid() && dateIsAfterToday(formData.date)) return true;
    }

    return false;
  };

  const validateAndCreate = () => {
    if (dataIsComplete()) {
      const request = {
        code: requestCode,
        colaborator_id: formData.colaborator,
        value: formData.amount.toString(),
        date: formatDate(formData.date),
        reason_id: formData.legalType,
        comments: formData.descripcion,
        receptor: formData.receptor,
      }

      if (isForEdit) {
        delete request.receptor;
        delete request.colaborator_id;
        delete request.value;
      } else {
        delete request.code;
      }

      handleRequest(request);
      if (!isForEdit) {
        handleCloseAndReload(true);
      }
    } else {
      if (!isForEdit) {
        const collaborator = colaborators.find((colab) => colab.code === formData.colaborator);

        if (dataIsComplete(false) && formData.amount && formData.amount <= collaborator.salary) {
          dispatch(openSnackbarAction({ msg: getLocalizedString("newSalaryMustBeGreaterThanPreviousSalary"), severity: "error" }));
        }
      }

      dispatch(openSnackbarAction({ msg: getLocalizedString("completeWithValidInformation"), severity: "error" }));
    }
  };

  const isAmountInRange = () => {
    if (!formData.amount) return false;

    const collaborator = colaborators.find((colab) => colab.code === formData.colaborator);
    if (!collaborator) return false;
    return formData.amount > collaborator.salary;
  }

  return (
    <>
      <DialogContent style={{ paddingTop: 0 }}>
        <Grid container direction="column" justify="center">
          {
            !isForEdit && !isReadOnly &&
            <Grid container item xs={12} alignItems="center" style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-receptor`}>{getLocalizedString("requestReceptor")}</InputLabel>
                <Select
                  disabled={isForEdit || isReadOnly}
                  value={formData.receptor}
                  labelId={`label-receptor`}
                  id={`select-receptor`}
                  onChange={(e) => setFormValue(e.target.value, "receptor")}
                  margin={"none"}
                >
                  {
                    recipients.raise.map((subItem) => (
                      <MenuItem
                        className={"whiteText"}
                        key={subItem.code}
                        value={subItem.code}>
                        {subItem.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
          }
          {
            !isForEdit && !isReadOnly &&
            <Grid container item xs={12} alignItems="center" style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-colaborator`}>{getLocalizedString("payRiseRequestedFor")}</InputLabel>
                <Select
                  onOpen={() => setUserSelectionDialogIsOpen(true)}
                  open={false}
                  disabled={isForEdit}
                  value={formData.colaborator}
                  labelId={`label-colaborator`}
                  id={`select-colaborator`}
                  margin={"none"}
                >
                  {
                    colaborators.map((subItem) => (
                      <MenuItem
                        className={"whiteText"}
                        key={subItem.code}
                        value={subItem.code}>
                        {subItem.value}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          }
          {
            (isForEdit || isReadOnly) &&
            <Grid container item xs={12} alignItems="center" style={gridStyle}>
              <TextField
                disabled
                label={getLocalizedString("payRiseRequestedFor")}
                value={formData.colaborator}
                margin={"none"}
                fullWidth
              />
            </Grid>
          }
          <Grid container item xs={12} alignItems="center">
            <Grid container item xs={12} sm={6} alignItems="center" style={gridStyle}>
              <TextField
                disabled
                label={getLocalizedString("actualSalary")}
                value={formData.actualSalary}
                margin={"none"}
                InputProps={{ inputComponent: CustomNumberFormat }}
                inputProps={{ min: "1", step: "0.1", decimals: 2 }}
                fullWidth
              />
            </Grid>
            <Grid container item xs={12} sm={6} alignItems="center" style={gridStyle} className="secondFieldPadding">
              <TextField
                label={getLocalizedString("salaryCoin")}
                disabled
                value={formData.actualSalaryCurrency}
                margin={"none"}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container item xs={12} alignItems="center">
            <Grid container item xs={12} sm={6} alignItems="center" style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <DatePicker
                  disabled={isReadOnly || !formData.colaborator}
                  required={!isReadOnly}
                  label={getLocalizedString("payRiseExpectedDate")}
                  clearable
                  clearLabel={getLocalizedString("clear")}
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  disablePast
                  minDate={getTomorrow()}
                  format="dd/MM/yyyy"
                  margin="none"
                  invalidDateMessage=''
                  minDateMessage=''
                  maxDateMessage=''
                  value={formData.date}
                  onChange={(e) => setFormValue(e, "date")}
                  fullWidth
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid container item xs={12} sm={6} alignItems="center" style={gridStyle} className="secondFieldPadding">
              <TextField
                required={!isReadOnly}
                label={getLocalizedString("newSalaryValue")}
                value={formData.amount}
                margin={"none"}
                disabled={(isForEdit || isReadOnly) ? true : !formData.colaborator}
                InputProps={{ inputComponent: CustomNumberFormat }}
                inputProps={{ min: "1", step: "0.1", decimals: 2 }}
                onChange={(e) => setFormValue(e.target.value.toString().slice(0, 1024), "amount")}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container item xs={12} alignItems="center" style={gridStyle}>
            <FormControl style={{ width: "100%" }}>
              <InputLabel required={!isReadOnly} id={`label-reasonToRegisterInDigitalFolder`}>
                {getLocalizedString("reasonToRegisterInDigitalFolder")}
              </InputLabel>
              <Select
                disabled={isReadOnly ? true : isForEdit ? false : !formData.colaborator}
                value={formData.legalType}
                labelId={`label-reasonToRegisterInDigitalFolder`}
                id={`select-reasonToRegisterInDigitalFolder`}
                onChange={(e) => setFormValue(e.target.value, "legalType")}
                margin={"none"}
              >
                {
                  raiseCalculationData.reasons.map((subItem) => (
                    <MenuItem
                      className={"whiteText"}
                      key={subItem.code}
                      value={subItem.code}>
                      {subItem.value}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid container item xs={12} alignItems="center" style={gridStyle}>
            <TextField
              required={!isReadOnly}
              label={getLocalizedString("requestedSalaryIncreaseJustificationDetail")}
              disabled={isReadOnly ? true : isForEdit ? false : !formData.colaborator}
              value={formData.descripcion}
              margin={"none"}
              onChange={(e) => setFormValue(e.target.value.toString().slice(0, 1024), "descripcion")}
              multiline={true}
              rows={1}
              rowsMax={5}
              fullWidth
            />
            {
              !isReadOnly &&
              <Typography
                style={{ paddingTop: 5 }}
                className={!textIsValid() ? "errorColor" : "whiteText"}
                variant="caption"
                gutterBottom
              >
                {`${getLocalizedString("currentChar")} ${formData.descripcion ? formData.descripcion.length : 0}. ${getLocalizedString("maxCharRequests")}.`}                        </Typography>
            }
          </Grid>
        </Grid>
      </DialogContent>
      {
        isReadOnly &&
        <div style={{ marginTop: 32 }} />
      }
      {
        !isReadOnly &&
        <DialogActions>
          <ButtonDialogAction
            onClick={handleCloseAndReload}
            text={getLocalizedString("disagree")}
          />
          <ButtonDialogAction
            onClick={validateAndCreate}
            text={getLocalizedString("agree")}
            isAccept
          />
        </DialogActions>
      }
      {
        userSelectionDialogIsOpen &&
        <SelectionDialog
          open={userSelectionDialogIsOpen}
          initialData={[formData.colaborator]}
          items={colaborators.map((item) => ({ ...item, key: item.code }))}
          getItemAvatar={(item) => item["FOTOGRAFIA"]}
          title={getLocalizedString("selectColabRise")}
          selectedTitle={getLocalizedString("selectedColab")}
          handleSubmit={(value) => setFormValue(value[0], "colaborator")}
          handleClose={() => setUserSelectionDialogIsOpen(false)}
          singleSelection
        />
      }
    </>
  );
}

export default NewPayRiseContent;